import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { SERVICES, SERVICE_GROUPS } from "@/data/site";
import { GUIDES, GUIDE_CATEGORIES } from "@/data/guides";
import { CITIES } from "@/data/cities";
import { useConsultation } from "@/context/ConsultationContext";

const svc = (slug) => SERVICES.find((s) => s.slug === slug);

const MENUS = [
  { key: "BUILD", type: "services", group: "BUILD" },
  { key: "MOVE", type: "services", group: "MOVE" },
  { key: "GROW", type: "services", group: "GROW" },
  { key: "LEARN", type: "learn" },
];

function MegaPanel({ menu, onNavigate }) {
  if (menu.type === "services") {
    const g = SERVICE_GROUPS[menu.group];
    return (
      <div className="grid grid-cols-[1fr_1.4fr] gap-8 p-8 w-[560px]">
        <div>
          <p className="accent-eyebrow text-gold">{g.label}</p>
          <p className="mt-3 font-playfair text-2xl text-warm leading-snug">{g.blurb}</p>
          <Link
            to={`/${menu.group.toLowerCase()}`}
            onClick={onNavigate}
            data-testid={`mega-${menu.group.toLowerCase()}-all`}
            className="mt-6 inline-flex items-center gap-1 text-sm text-gold hover:gap-2 transition-all"
          >
            Explore {g.label} <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 gap-1">
          {g.slugs.map((slug) => {
            const s = svc(slug);
            return (
              <Link
                key={slug}
                to={`/services/${slug}`}
                onClick={onNavigate}
                data-testid={`mega-link-${slug}`}
                className="group rounded-lg px-4 py-3 hover:bg-warm/5 transition-colors"
              >
                <span className="block text-warm font-500 group-hover:text-gold transition-colors">{s.title}</span>
                <span className="block text-xs text-warm/50 mt-0.5">{s.tagline}</span>
              </Link>
            );
          })}
        </div>
      </div>
    );
  }
  // LEARN
  return (
    <div className="grid grid-cols-[1.2fr_1fr] gap-8 p-8 w-[620px]">
      <div>
        <p className="accent-eyebrow text-gold">Knowledge Hub</p>
        <div className="mt-4 grid gap-1">
          {GUIDES.slice(0, 5).map((gd) => (
            <Link
              key={gd.slug}
              to={`/guides/${gd.slug}`}
              onClick={onNavigate}
              data-testid={`mega-guide-${gd.slug}`}
              className="group rounded-lg px-3 py-2.5 hover:bg-warm/5 transition-colors"
            >
              <span className="block text-warm text-sm font-500 group-hover:text-gold transition-colors">{gd.title}</span>
            </Link>
          ))}
        </div>
        <Link to="/learn" onClick={onNavigate} data-testid="mega-learn-all" className="mt-4 inline-flex items-center gap-1 text-sm text-gold hover:gap-2 transition-all">
          All guides <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
      <div>
        <p className="accent-eyebrow text-gold">City Guides</p>
        <div className="mt-4 grid grid-cols-2 gap-1">
          {CITIES.map((c) => (
            <Link
              key={c.slug}
              to={`/cities/${c.slug}`}
              onClick={onNavigate}
              data-testid={`mega-city-${c.slug}`}
              className="group rounded-lg px-3 py-2.5 hover:bg-warm/5 transition-colors"
            >
              <span className="block text-warm text-sm font-500 group-hover:text-gold transition-colors">{c.name}</span>
              <span className="block text-[11px] text-warm/45">{c.bestFor}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSub, setMobileSub] = useState(null);
  const { openConsultation } = useConsultation();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setActive(null);
  }, [location.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-nav bg-navy/90 border-b border-warm/10 py-3" : "bg-navy/70 glass-nav py-5"
      }`}
      onMouseLeave={() => setActive(null)}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between">
        <Logo variant="light" />

        <nav className="hidden lg:flex items-center gap-1">
          {MENUS.map((m) => (
            <div key={m.key} className="relative" onMouseEnter={() => setActive(m.key)}>
              <button
                data-testid={`nav-${m.key.toLowerCase()}`}
                className={`flex items-center gap-1 px-4 py-2 text-sm tracking-wide transition-colors ${
                  active === m.key ? "text-gold" : "text-warm/80 hover:text-warm"
                }`}
              >
                {m.key} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${active === m.key ? "rotate-180" : ""}`} />
              </button>
            </div>
          ))}
          <Link to="/about" data-testid="nav-about" className="px-4 py-2 text-sm tracking-wide text-warm/80 hover:text-warm transition-colors">ABOUT</Link>
          <Link to="/contact" data-testid="nav-contact" className="px-4 py-2 text-sm tracking-wide text-warm/80 hover:text-warm transition-colors">CONTACT</Link>
        </nav>

        <div className="hidden lg:block">
          <button
            data-testid="nav-cta-start"
            onClick={() => openConsultation()}
            className="rounded-full bg-gold px-6 py-2.5 text-sm font-600 text-navy hover:bg-gold-soft transition-all hover:scale-105 active:scale-95"
          >
            Start Your Journey
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-warm p-2"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Menu"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Desktop mega dropdown */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.22 }}
            className="absolute left-1/2 -translate-x-1/2 mt-3 hidden lg:block"
            onMouseEnter={() => setActive(active)}
          >
            <div className="rounded-2xl border border-warm/10 bg-navy-deep/95 glass-nav shadow-2xl shadow-black/40 overflow-hidden">
              <MegaPanel menu={MENUS.find((m) => m.key === active)} onNavigate={() => setActive(null)} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden overflow-hidden bg-navy-deep border-t border-warm/10"
          >
            <div className="max-h-[75vh] overflow-y-auto px-5 py-4">
              {MENUS.map((m) => (
                <div key={m.key} className="border-b border-warm/5">
                  <button
                    data-testid={`mobile-nav-${m.key.toLowerCase()}`}
                    onClick={() => setMobileSub(mobileSub === m.key ? null : m.key)}
                    className="w-full flex items-center justify-between py-4 text-warm"
                  >
                    <span className="tracking-wide">{m.key}</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${mobileSub === m.key ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence>
                    {mobileSub === m.key && (
                      <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="overflow-hidden">
                        <div className="pb-4 pl-2 grid gap-1">
                          {m.type === "services"
                            ? SERVICE_GROUPS[m.group].slugs.map((slug) => (
                                <Link key={slug} to={`/services/${slug}`} className="py-2 text-sm text-warm/70">{svc(slug).title}</Link>
                              ))
                            : (
                              <>
                                {GUIDES.map((g) => <Link key={g.slug} to={`/guides/${g.slug}`} className="py-2 text-sm text-warm/70">{g.title}</Link>)}
                                {CITIES.map((c) => <Link key={c.slug} to={`/cities/${c.slug}`} className="py-2 text-sm text-warm/70">{c.name} — City Guide</Link>)}
                              </>
                            )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
              <Link to="/about" className="block py-4 text-warm border-b border-warm/5">ABOUT</Link>
              <Link to="/contact" className="block py-4 text-warm border-b border-warm/5">CONTACT</Link>
              <button
                data-testid="mobile-cta-start"
                onClick={() => { setMobileOpen(false); openConsultation(); }}
                className="mt-5 w-full rounded-full bg-gold py-3 font-600 text-navy"
              >
                Start Your Journey
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
